import { cn } from "@/lib/utils";

/** Height of the mobile dock (icons row + inner padding, excluding safe-area). */
export const MOBILE_BOTTOM_NAV_HEIGHT_REM = "4.25rem";

/**
 * Fixed bottom bar — includes horizontal + bottom safe-area insets for
 * notched / home-indicator devices (iPhone, etc.).
 */
export const mobileBottomNavBarClassName =
  "lg:hidden fixed bottom-[-1px] left-0 right-0 z-50 border-t border-primary/15 bg-card/95 backdrop-blur-md supports-[backdrop-filter]:bg-card/90 dark:border-border dark:bg-background/95 dark:supports-[backdrop-filter]:bg-background/80 pointer-events-auto translate-y-0 opacity-100 pl-[max(0px,env(safe-area-inset-left))] pr-[max(0px,env(safe-area-inset-right))] pb-[env(safe-area-inset-bottom,0px)]";

/** Inner grid row above the home-indicator safe area. */
export const mobileBottomNavInnerClassName =
  "grid h-16 pb-2.5";

/**
 * Extra bottom padding for page content / footer so it clears the mobile dock
 * when the home-style bottom nav is enabled.
 */
export const mobileBottomNavContentInsetClassName = cn(
  "max-lg:pb-[calc(3rem+4.25rem+env(safe-area-inset-bottom,0px))]",
);

export const mobileBottomNavFooterInsetClassName = cn(
  "max-lg:pb-[calc(4.25rem+env(safe-area-inset-bottom,0px))]",
);

/**
 * Auth / login cards: top-aligned. Mobile stays compact; desktop grows so the
 * site footer sits at the bottom of the viewport (see root layout `main`).
 */
export const authPageShellClassName = cn(
  "flex w-full items-start justify-center px-3 pt-6 pb-6 sm:px-4 sm:pt-8",
  "max-lg:flex-none lg:flex-1 lg:pt-10 lg:pb-10",
);

export function mobileBottomNavItemClassName(active: boolean) {
  return cn(
    "relative flex min-w-0 max-w-full flex-col items-center justify-center gap-1 rounded-lg px-1.5 py-1 transition-colors",
    active
      ? "font-semibold text-foreground"
      : "text-muted-foreground hover:text-foreground",
  );
}

/** Badge on the Sačuvani (heart) tab — same offset on homepage and service detail. */
export const mobileBottomNavFavCountBadgeClassName =
  "absolute flex h-[18px] min-w-[18px] items-center justify-center rounded-full border border-background bg-primary px-1 text-[10px] font-bold leading-none text-primary-foreground shadow-sm left-[calc(50%--5px)] top-[3px]";

export const SCROLL_TO_SERVICES_FLAG_KEY = "planner-scroll-to-services-smooth";

/** Scroll marketplace `#usluge` into view; returns false if section not in DOM yet. */
export function scrollToServicesSection(
  behavior: ScrollBehavior = "smooth",
): boolean {
  if (typeof document === "undefined") return false;
  const el = document.getElementById("usluge");
  if (!el) return false;
  el.scrollIntoView({ behavior, block: "start" });
  return true;
}

/** Retry scroll until `#usluge` mounts (e.g. after client nav from another page). */
export function scrollToServicesSectionWhenReady(
  opts?: { maxAttempts?: number; intervalMs?: number; behavior?: ScrollBehavior },
): void {
  if (typeof window === "undefined") return;
  const maxAttempts = opts?.maxAttempts ?? 30;
  const intervalMs = opts?.intervalMs ?? 100;
  let attempts = 0;
  const tryScroll = () => {
    if (scrollToServicesSection(opts?.behavior ?? "smooth")) return;
    attempts += 1;
    if (attempts < maxAttempts) window.setTimeout(tryScroll, intervalMs);
  };
  tryScroll();
}

/** Set scroll flag and append `#usluge` for cross-page navigation to filtered catalog. */
export function hrefForServicesCatalog(pathWithQuery: string): string {
  if (typeof window !== "undefined") {
    window.sessionStorage.setItem(SCROLL_TO_SERVICES_FLAG_KEY, "1");
  }
  const [pathAndQuery, hash] = pathWithQuery.split("#");
  if (hash === "usluge") return pathWithQuery;
  return `${pathAndQuery}#usluge`;
}

/** `pathname.startsWith("/usluge/")` — single service pages use `ServiceDetailMobileNav`. */
export function isServiceDetailPath(pathname: string): boolean {
  return pathname.startsWith("/usluge/");
}

/** Auth / registration routes that use the marketplace-style bottom nav. */
export const MOBILE_HOME_BOTTOM_AUTH_PATHS = [
  "/prijava",
  "/potvrda-emaila",
  "/reset-lozinke",
  "/zaboravljena-lozinka",
  "/kreiraj-svadbu",
] as const;

export function isAuthMobileBottomNavPath(pathname: string): boolean {
  return MOBILE_HOME_BOTTOM_AUTH_PATHS.some((p) => pathname === p);
}

/** Show home-style bottom nav (home, auth pages; not service detail — has its own). */
export function usesHomeMobileBottomNav(pathname: string): boolean {
  if (pathname === "/") return true;
  if (isServiceDetailPath(pathname)) return true;
  return isAuthMobileBottomNavPath(pathname);
}
